import { Vehicle } from "@/types/vehicle";

const API_BASE_URL = process.env.REACT_APP_API_HOST;

class ApiService {
  /**
   * Start a new session with the AI service
   * @param vehicleInfo Optional vehicle information to include in the session
   * @returns The session ID
   */
  async startSession(vehicle: Vehicle, token: string|null = ""): Promise<string> {
    try {
      console.log('Starting new session...');
      const params = new URLSearchParams({make: vehicle.make, model: vehicle.model, type: vehicle.type}).toString();
      const options = {
        headers: token
          ? { 'Authorization': `Bearer ${token}` }
          : undefined
      };
      const response = await fetch(`${API_BASE_URL}/session/start?${params}`, options);

      if (!response.ok) {
        // Throw the response object directly for proper error handling
        throw response;
      }

      const data = await response.json();

      if (typeof data === 'string') {
        console.log('Session ID is:', data);
        return data;
      } else {
        console.error('Invalid session response format:', data);
        throw new Error('Invalid session response format');
      }
    } catch (error) {
      console.error('Failed to start session:', error);
      throw error;
    }
  }

  async sendMessage(
    chatId: string,
    content: string,
    onStream: (chunk: string) => void,
    token: string | null = ""
  ): Promise<void> {
    if (!chatId) {
      throw new Error('No active session. Please start a new session before sending a message.');
    }

    console.log('🚀 Starting streaming request...');
    
    return this.withRetry(async () => {
      // Create an AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏰ Request timeout, aborting...');
        controller.abort();
      }, 50000); // 50 second timeout

      try {
        const response = await fetch(`${API_BASE_URL}/chat/sse`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            session_id: chatId,
            user_query: content
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        console.log('📡 Response received:', response.status, response.statusText);
        console.log('📋 Response headers:', Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
          console.error('❌ Response not ok:', response.status, response.statusText);
          throw response;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          // Fallback: Try to read response as text (React Native sometimes doesn't support streaming)
          try {
            const responseText = await response.text();
            if (responseText) {
              this.processSSEBuffer(responseText, onStream);
            } else {
              throw this.createRetryableError('No data received from server');
            }
          } catch (error) {
            throw this.createRetryableError('Failed to read response');
          }
        } else {
          const decoder = new TextDecoder("utf-8");
          let buffer = '';
          let hasReceivedData = false;
          let chunkCount = 0;
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                break;
              }
              if (value) {
                hasReceivedData = true;
                chunkCount++;
                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;
                buffer = this.processSSEBuffer(buffer, onStream);
              }
            }
            if (!hasReceivedData) {
              throw new Error('No data received from streaming endpoint');
            }
            if (buffer.trim()) {
              this.processSSEBuffer(buffer + '\n\n', onStream);
            }
          } catch (readError: any) {
            if (readError.name === 'AbortError') {
              throw new Error('Request timed out. Please try again.');
            }
            throw readError;
          } finally {
            try {
              reader.releaseLock();
            } catch (releaseError: any) {
              // ignore
            }
          }
        }
      } finally {
        clearTimeout(timeoutId);
      }
    }, { maxRetries: 2, baseDelay: 1000 });
  }

  /**
   * Get the title for a chat based on the provided query
   * @param query The query to generate the chat title from
   * @param token Optional authentication token
   * @returns The generated chat title
   */
  async getChatTitle(query: string, token: string | null = ""): Promise<string> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/chat/title`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_query: query })
      });

      if (!response.ok) {
        throw response;
      }

      const data = await response.json();
      return data.title || "New Chat";
    } catch (error) {
      console.error('Failed to fetch chat title:', error);
      throw error;
    }
  }

  /**
   * Get all available vehicles from the API
   * @returns Promise<VehicleMake[]> The list of vehicle makes, models, and types
   */
  async getVehicles(token: string | null = ""): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/vehicles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw response;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      throw error;
    }
  }

  /**
   * Rebuild a session from chat history when the original session is expired
   * @param vehicle Vehicle information
   * @param messageHistory Array of previous messages
   * @param token Authentication token
   * @returns New session ID
   */
  async rebuildSession(
    vehicle: Vehicle, 
    messageHistory: Array<{role: string, content: string, timestamp: number}>,
    token: string | null = ""
  ): Promise<string> {
    try {
      console.log('Rebuilding session from chat history...');
      const payload = {
        vehicle_info: {
          make: vehicle.make,
          model: vehicle.model,
          type: vehicle.type
        },
        message_history: messageHistory
      };

      const response = await fetch(`${API_BASE_URL}/session/rebuild`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw response;
      }

      const data = await response.json();
      console.log('Session rebuilt successfully. New session ID:', data.new_session_id);
      return data.new_session_id;
    } catch (error) {
      console.error('Failed to rebuild session:', error);
      throw error;
    }
  }

  handleError(error: Error | Response): { status?: number; message: string } {
    if (error instanceof Response) {
      switch (error.status) {
        case 401:
          console.error('Unauthorized access:', error.statusText);
          break;
        case 403:
          console.error('Forbidden access:', error.statusText);
          break;
        case 429:
          console.error('Rate limit exceeded:', error.statusText);
          break;
        default:
          console.error('API Error:', error.status, error.statusText);
      }
      return { status: error.status, message: error.statusText };
    } else {
      console.error('Error:', error.message);
      return { message: error.message };
    }
  }

  /**
   * Check if an error is a rate limit error (429)
   */
  isRateLimitError(error: any): boolean {
    return (error instanceof Response && error.status === 429) || 
           (error?.status === 429) ||
           (error?.message && error.message.includes('429'));
  }

  /**
   * Check if an error is a session expired error (410)
   */
  isSessionExpiredError(error: any): boolean {
    return (error instanceof Response && error.status === 410) || 
           (error?.status === 410) ||
           (error?.error === 'session_expired');
  }

  /**
   * Retry logic for API calls
   */
  private async withRetry<T>(
    operation: () => Promise<T>,
    options: { maxRetries: number; baseDelay: number } = { maxRetries: 2, baseDelay: 1000 }
  ): Promise<T> {
    let lastError: any;
    
    for (let attempt = 0; attempt <= options.maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        if (attempt === options.maxRetries) {
          throw error;
        }
        
        // Wait before retrying with exponential backoff
        const delay = options.baseDelay * Math.pow(2, attempt);
        console.log(`Retrying in ${delay}ms... (attempt ${attempt + 1}/${options.maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  }

  /**
   * Create a retryable error
   */
  private createRetryableError(message: string): Error {
    const error = new Error(message);
    (error as any).retryable = true;
    return error;
  }

  /**
   * Process Server-Sent Events buffer
   */
  private processSSEBuffer(buffer: string, emit: (s: string) => void): string {
    let eventEndIndex: number;
    while ((eventEndIndex = buffer.indexOf('\n\n')) !== -1 || buffer.includes('\ndata:')) {
      let rawEvent: string;
      if (eventEndIndex !== -1) {
        rawEvent = buffer.slice(0, eventEndIndex);
        buffer = buffer.slice(eventEndIndex + 2);
      } else {
        const nextDataIndex = buffer.indexOf('\ndata:', 1);
        if (nextDataIndex === -1) {
          break;
        }
        rawEvent = buffer.slice(0, nextDataIndex);
        buffer = buffer.slice(nextDataIndex + 1);
      }
      const lines = rawEvent.split('\n').map(line => line.trim()).filter(line => line);
      for (const line of lines) {
        if (line.startsWith('data:')) {
          const data = line.slice(5).trim();
          if (data === '[DONE]') {
            continue;
          }
          if (data) {
            try {
              const parsed = JSON.parse(data);
              if (parsed?.type === 'chunk' && typeof parsed.content === 'string') {
                emit(parsed.content);
              } else if (parsed?.type === 'start') {
                // Handle start events if needed
              } else if (parsed?.type === 'complete') {
                // Handle complete events if needed
              } else if (parsed?.type === 'error') {
                console.warn('SSE error frame received:', parsed);
              } else {
                console.warn('Unknown SSE frame type, skipping:', parsed);
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE frame, skipping:', data, parseError);
            }
          }
        }
      }
    }
    const remainingLines = buffer.split('\n');
    for (let i = 0; i < remainingLines.length - 1; i++) {
      const line = remainingLines[i].trim();
      if (line.startsWith('data:')) {
        const data = line.slice(5).trim();
        if (data) {
          try {
            const parsed = JSON.parse(data);
            if (parsed?.type === 'chunk' && typeof parsed.content === 'string') {
              emit(parsed.content);
              remainingLines[i] = '';
            }
          } catch (parseError) {
            // Keep the line for later processing
          }
        }
      }
    }
    const newBuffer = remainingLines.filter(line => line.trim()).join('\n');
    return newBuffer;
  }
}

export const apiService = new ApiService();